import { useTrackLastLocation } from '@hooks/navigation';
import { useDispatch } from 'react-redux';
import { getIsRequisitionStatusHandled } from '@config';
import { REQUISITION, USER } from '@config/constants';
import { setAuthenticated } from '@reducks/session';
import { useChat } from './useChat';
import { useLoggerContext } from './useLoggerContext';
import { useUserQuery } from './useUserQuery';
import { useTrackerContext } from './useTrackerContext';
import { useRedirect } from './redirect/useRedirect';
import { useAllowedRoute } from './useAllowedRoute';
import {
  getIsClabeRequisitionPath,
  getIsCancelledRequisitionPath,
  getIsDocumentsRequisitionPath,
  getIsReferencesRequisitionPath,
} from '../Credit/utils';

const getSkipEmailValidation = ({
  role,
  userStatus,
  requisition,
  isEmailConfirmed,
}) => {
  if (isEmailConfirmed === true) return true;
  if (role === USER.ROLES.INVESTOR) {
    return userStatus !== USER.STATUS.STARTED;
  }
  if (role === USER.ROLES.BORROWER) {
    if (!requisition) return false;
    if (requisition.status === REQUISITION.EMAIL_REQUIRED_STATUS.CLOSE_CREDIT) return false;
    // Prevent keep on web app unless requisition is on a handled status
    // code below takes precedence over the one above
    // line code duplicated to force redirection to borrower app
    // https://vscode.dev/github/yotepresto-com/web-app/blob/main/src/navigators/Root/App/hooks/redirect/useRedirect.js#L37
    return !getIsRequisitionStatusHandled(requisition?.status);
  }

  return false;
};

export const useController = () => {
  const dispatch = useDispatch();
  const { data, loading: loadingUser } = useUserQuery({
    onCompleted: () => {
      dispatch(setAuthenticated());
    },
  });
  const user = data?.user || {};
  const activeRequisition = data?.activeRequisition || null;
  const {
    email,
    flags,
    status,
    id: userId,
    roleName: role,
  } = user;
  const {
    isEmailConfirmed,
    isPersonConfirmed,
    forcePasswordUpdate,
    isRecoveringPassword,
  } = flags || {};

  const requisitionStatus = activeRequisition?.status || '';

  const skipEmailValidation = loadingUser
    || getSkipEmailValidation({
      userStatus: status, role, requisition: activeRequisition, isEmailConfirmed,
    });

  const resolverOptions = {
    skipEmailValidation,
  };

  const { isLoadingRedirection } = useRedirect({
    role,
    userId,
    status,
    isEmailConfirmed,
    activeRequisition,
    isPersonConfirmed,
    requisitionStatus,
    skipEmailValidation,
    forcePasswordUpdate,
    isRecoveringPassword,
  });
  const isLoading = loadingUser || isLoadingRedirection;

  const isCancelledRequisition = getIsCancelledRequisitionPath(
    { status: requisitionStatus },
  );
  const isReferencesRequisition = getIsReferencesRequisitionPath(
    { status: requisitionStatus },
  );
  const isDocumentsRequisition = getIsDocumentsRequisitionPath(
    { status: requisitionStatus },
  );
  const isClabeRequisition = getIsClabeRequisitionPath(
    { status: requisitionStatus },
  );

  const allowedRouteMap = useAllowedRoute({
    isEmailConfirmed,
    skipEmailValidation,
    forcePasswordUpdate,
    isRecoveringPassword,
    isCancelledRequisition,
    isReferencesRequisition,
    isDocumentsRequisition,
    isClabeRequisition,
    requisitionStatus,
  });

  useChat({ role });
  useLoggerContext({ userId });
  useTrackerContext({ userId, email, role });
  useTrackLastLocation({ userId });

  return {
    isLoading,
    allowedRouteMap,
    resolverOptions,
  };
};
